import { type SchedulerSettings, type SchedulerView } from "./settings";
import { type ScopeMode, schedulerScopeKey } from "./settings-document";
import type { ClinicId, Provider, ProviderId } from "./types";

/**
 * ENG-2666: a person's classic scheduler setup, read once when they switch to V3, as the
 * settings V3 would give them.
 *
 * Pure: the raw strings come in from `data/classic-settings-source.ts` (the one file allowed
 * to read classic's localStorage) and a plan goes out. Nothing here knows about storage,
 * the store or the server. Every key below is cited to where the frozen classic module
 * writes it; V3 never imports that module.
 *
 * None of these keys names a user: they are global, or keyed by clinic or region, and
 * logout clears none of them. So a value here is "what classic shows on this computer",
 * and it is saved only after the person has seen it (AC 7, Chen's D1 = B).
 */

/** Every classic key that is read, and nothing else. All written by `setupLocalStorageAPI` (JSON). */
export const CLASSIC_KEYS = {
  /** "Classic View" — `scheduler-slice.ts:88`, written `:473`. */
  classicView: "scheduleViewMode",
  /** "Day" | "Week" | "Work Week" | "Month" — `scheduler-slice.ts:90`, written `:466`. */
  view: "currentViewName",
  /** "Show By Clinic" — `scheduler-slice.ts:94-96`. */
  showByClinic: "scheduleShowByClinicViewMode",
  /** "Include Supervising Appointment" — `scheduler-slice.ts:102-104`. */
  includeSupervising: "scheduleIncludeSupervisingProvider",
  /** "Show Side Panel" — `scheduler-slice.ts:92`. Classic's default is visible. */
  sidePanelVisible: "filtersPanelVisible",
  /** "Provider only" in the staff filter — `use-staff-filter.hook.tsx:131`. */
  providersOnly: "showAudiologistsOnly",
  /** `{ "clinic:<id>" | "region:<id>": { clinics: id[], staff: {id, checked}[] } }` — `use-storage.hook.ts:15`. */
  filters: "clinic-filters",
  /** `{ <same key>: {id, index}[] }`, or an older single list — `use-providers-order-storage.hook.ts:18,31-35`. */
  order: "schedulingProvidersOrder"
} as const;

export type ClassicKey = (typeof CLASSIC_KEYS)[keyof typeof CLASSIC_KEYS];

/** The raw stored strings. A key classic never wrote is absent. */
export type ClassicRaw = Partial<Record<ClassicKey, string>>;

/** Taken at the switch, handed to V3 in memory. `raw: null` means the read itself failed. */
export interface ClassicSnapshot {
  userId: number;
  practiceId: number;
  raw: ClassicRaw | null;
}

/** How the schedule looks and opens. Low-harm if they turn out to be another person's. */
export const LOOK_KEYS = [
  "cardScheme",
  "columnShading",
  "view",
  "grouping",
  "sidePanelCollapsed",
  "includeSupervising",
  "showAllStaff"
] as const satisfies readonly (keyof SchedulerSettings)[];
export type LookKey = (typeof LOOK_KEYS)[number];
export type LookValues = Partial<Pick<SchedulerSettings, LookKey>>;

/** Who the person sees, per clinic or region. The personal half. */
export type SelectionKey =
  | "clinicIds"
  | "staffListIds"
  | "providerIds"
  | "columnOrder"
  | "staffPreset";
export type SelectionValues = Partial<Pick<SchedulerSettings, SelectionKey>>;

export interface CarryOverPlan {
  look: LookValues;
  /** Keyed by V3 bag (`schedulerScopeKey`): the one on screen and any others classic had. */
  bags: Record<string, SelectionValues>;
  /** The bag on screen now. */
  currentBag: string;
  /** People shown in classic here who are gone or inactive, left out of the copy (AC 6). */
  droppedStaffIds: ProviderId[];
}

const parse = (raw: string | undefined): unknown => {
  if (raw == null) return undefined;
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
};

const bool = (raw: string | undefined): boolean | undefined => {
  const value = parse(raw);
  return typeof value === "boolean" ? value : undefined;
};

const isId = (value: unknown): value is number =>
  Number.isInteger(value) && (value as number) > 0;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const CLASSIC_VIEWS: Record<string, SchedulerView> = {
  Day: "day",
  Week: "week",
  "Work Week": "workWeek",
  Month: "month"
};

/** Only what classic actually stored: a key it never wrote stays on V3's default. */
export function readClassicLook(raw: ClassicRaw): LookValues {
  const look: LookValues = {};
  // Classic View is two V3 settings (Chen, 2026-11-03). Off, both stay on V3's defaults.
  if (bool(raw[CLASSIC_KEYS.classicView]) === true) {
    look.cardScheme = "filled";
    look.columnShading = "plain";
  }
  const view = parse(raw[CLASSIC_KEYS.view]);
  if (typeof view === "string" && CLASSIC_VIEWS[view])
    look.view = CLASSIC_VIEWS[view];
  const byClinic = bool(raw[CLASSIC_KEYS.showByClinic]);
  if (byClinic !== undefined) look.grouping = byClinic ? "clinic" : "provider";
  const panel = bool(raw[CLASSIC_KEYS.sidePanelVisible]);
  if (panel !== undefined) look.sidePanelCollapsed = !panel;
  const supervising = bool(raw[CLASSIC_KEYS.includeSupervising]);
  if (supervising !== undefined) look.includeSupervising = supervising;
  const providersOnly = bool(raw[CLASSIC_KEYS.providersOnly]);
  if (providersOnly !== undefined) look.showAllStaff = !providersOnly;
  return look;
}

interface ClassicEntry {
  clinics?: number[];
  staff?: { id: number; checked: boolean }[];
  order?: number[];
}

const BAG_KEY = /^(clinic|region):\d+$/;

/** `{id, index}[]` → ids in column order; anything malformed is dropped. */
const orderOf = (value: unknown): number[] | undefined =>
  Array.isArray(value)
    ? value
        .filter((o) => isObject(o) && isId(o.id) && Number.isFinite(o.index))
        .sort((a, b) => (a.index as number) - (b.index as number))
        .map((o) => o.id as number)
    : undefined;

/** Classic's two per-clinic maps, merged by key. */
function readEntries(raw: ClassicRaw): {
  entries: Record<string, ClassicEntry>;
  legacyOrder?: number[];
} {
  const entries: Record<string, ClassicEntry> = {};
  const filters = parse(raw[CLASSIC_KEYS.filters]);
  if (isObject(filters)) {
    for (const [key, value] of Object.entries(filters)) {
      if (!BAG_KEY.test(key) || !isObject(value)) continue;
      const entry: ClassicEntry = {};
      if (Array.isArray(value.clinics))
        entry.clinics = value.clinics.filter(isId);
      if (Array.isArray(value.staff))
        entry.staff = value.staff
          .filter((s) => isObject(s) && isId(s.id))
          .map((s) => ({ id: s.id as number, checked: s.checked === true }));
      entries[key] = entry;
    }
  }
  const order = parse(raw[CLASSIC_KEYS.order]);
  // The older shape is one list that classic applied to whatever key it was on.
  if (Array.isArray(order)) return { entries, legacyOrder: orderOf(order) };
  if (isObject(order)) {
    for (const [key, value] of Object.entries(order)) {
      if (!BAG_KEY.test(key)) continue;
      entries[key] = { ...entries[key], order: orderOf(value) };
    }
  }
  return { entries };
}

export interface CarryOverContext {
  scopeMode: ScopeMode;
  hasRegions: boolean;
  /** The header clinic, which is what classic keyed its storage on. */
  currentClinicId: ClinicId | null;
  regionId: number | null;
  /** Clinics this person can see today. */
  clinicIds: readonly ClinicId[];
  /** Everyone the catalog loaded, inactive included. */
  providers: readonly Pick<Provider, "id" | "isActive">[];
}

const unique = (ids: number[]): number[] => [...new Set(ids)];

/**
 * Classic's entries → V3 bags (D6: every clinic's, not just this one's).
 *
 * Classic keys by `region:<id>` only for a region practice whose user does not keep a
 * selection per clinic (`scheduler-storage-key.ts:18-24`); otherwise by `clinic:<id>`. V3
 * keys by `clinic:<id>` in clinic mode and by `region:<id>` or `all` otherwise
 * (`schedulerScopeKey`). So a classic entry is copied where the two agree, and the bag on
 * screen also takes the header clinic's own entry when they do not.
 */
function sourcesFor(
  entries: Record<string, ClassicEntry>,
  legacyOrder: number[] | undefined,
  currentBag: string,
  ctx: CarryOverContext
): Record<string, ClassicEntry> {
  const sources: Record<string, ClassicEntry> = {};
  const prefix =
    ctx.scopeMode === "clinic" ? "clinic:" : ctx.hasRegions ? "region:" : null;
  if (prefix) {
    for (const [key, entry] of Object.entries(entries)) {
      if (key.startsWith(prefix)) sources[key] = entry;
    }
  }
  const own =
    ctx.currentClinicId != null
      ? entries[`clinic:${ctx.currentClinicId}`]
      : undefined;
  if (!sources[currentBag] && own) sources[currentBag] = own;
  if (legacyOrder && !sources[currentBag]?.order) {
    sources[currentBag] = { ...sources[currentBag], order: legacyOrder };
  }
  return sources;
}

/** One classic entry as V3 selections, filtered to today's clinics and active staff. */
function selectionOf(
  entry: ClassicEntry,
  knownClinics: ReadonlySet<number>,
  active: ReadonlySet<number>,
  dropped: Set<number> | null
): SelectionValues {
  const bag: SelectionValues = {};
  const clinics = entry.clinics?.filter((id) => knownClinics.has(id)) ?? [];
  if (clinics.length) bag.clinicIds = unique(clinics);
  const staff = entry.staff ?? [];
  // AC 6: gone or inactive staff are left out. Only the ones classic SHOWED are reported.
  staff
    .filter((s) => s.checked && !active.has(s.id))
    .forEach((s) => dropped?.add(s.id));
  const kept = staff.filter((s) => active.has(s.id));
  const shown = unique(kept.filter((s) => s.checked).map((s) => s.id));
  // A list with nobody ticked would draw an empty grid; the clinic's team is the better start.
  if (shown.length) {
    bag.staffListIds = unique(kept.map((s) => s.id));
    bag.providerIds = shown;
    // A hand-built list, as classic's is: restored as saved, never re-derived (ENG-2047 D4).
    bag.staffPreset = null;
  }
  const order = entry.order?.filter((id) => active.has(id)) ?? [];
  if (order.length) bag.columnOrder = unique(order);
  return bag;
}

export function planCarryOver(
  raw: ClassicRaw,
  ctx: CarryOverContext
): CarryOverPlan {
  const currentBag = schedulerScopeKey({
    scopeMode: ctx.scopeMode,
    currentClinicId: ctx.currentClinicId,
    regionId: ctx.regionId,
    hasRegions: ctx.hasRegions
  });
  const { entries, legacyOrder } = readEntries(raw);
  const knownClinics = new Set(ctx.clinicIds);
  const active = new Set(
    ctx.providers.filter((p) => p.isActive).map((p) => p.id)
  );
  const dropped = new Set<number>();
  const bags: Record<string, SelectionValues> = {};
  for (const [key, entry] of Object.entries(
    sourcesFor(entries, legacyOrder, currentBag, ctx)
  )) {
    const bag = selectionOf(
      entry,
      knownClinics,
      active,
      key === currentBag ? dropped : null
    );
    if (Object.keys(bag).length) bags[key] = bag;
  }
  return {
    look: readClassicLook(raw),
    bags,
    currentBag,
    droppedStaffIds: [...dropped]
  };
}

/** AC 7's fallback when the person has not seen the values: the look, never the people. */
export const lookOnly = (plan: CarryOverPlan): CarryOverPlan => ({
  ...plan,
  bags: {},
  droppedStaffIds: []
});

/** What lands on screen now: the look plus the bag on screen. */
export const valuesOnScreen = (
  plan: CarryOverPlan
): Partial<SchedulerSettings> => ({
  ...plan.look,
  ...plan.bags[plan.currentBag]
});
